import PropTypes from "prop-types";
import { useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
// @mui
import { CssBaseline } from "@mui/material";
import {
    ThemeProvider as MUIThemeProvider,
    createTheme,
    StyledEngineProvider,
} from "@mui/material/styles";
//
import palette from "./palette";
import shadows from "./shadows";
import typography from "./typography";
import GlobalStyles from "./globalStyles";
import customShadows from "./customShadows";
import componentsOverride from "./overrides";
import { useLocalization } from "../i18n/LocalizationProvider";

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
    children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
    const { dir } = useLocalization();

    const cache = useMemo(() => {
        const isRTL = dir === "rtl";
        return createCache({
            key: isRTL ? "muirtl" : "mui",
            stylisPlugins: isRTL ? [prefixer, rtlPlugin] : [prefixer],
        });
    }, [dir]);

    const themeOptions = useMemo(
        () => ({
            direction: dir,
            palette,
            shape: { borderRadius: 6 },
            typography,
            shadows: shadows(),
            customShadows: customShadows(),
        }),
        [dir]
    );

    const theme = useMemo(() => {
        const t = createTheme(themeOptions);
        t.components = componentsOverride(t);
        return t;
    }, [themeOptions]);

    return (
        <CacheProvider value={cache}>
            <StyledEngineProvider injectFirst>
                <MUIThemeProvider theme={theme}>
                    <CssBaseline />
                    <GlobalStyles />
                    {children}
                </MUIThemeProvider>
            </StyledEngineProvider>
        </CacheProvider>
    );
}
